import type { Customer } from '../contexts/CustomerAuthContext';

export interface RecentOrder {
  id: string;
  createdAt: string;
  totalAmount: number;
  items: { name: string; quantity: number }[];
}

export interface RestaurantProgress {
  id: string;
  name: string;
  orders: number;
  lastOrder: string;
  visitsThisMonth: number;
  achievementProgress: {
    unlocked: number;
    total: number;
    pct: number;
    nextAchievementName: string | null;
  } | null;
  recentOrders: RecentOrder[];
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon?: string | null;
  points: number;
  unlockedAt: string;
  notifiedAt: string | null;
}

export interface UpcomingAchievement {
  id: string;
  name: string;
  description: string;
  icon?: string | null;
  points: number;
  progress: number;
  currentValue: number;
  targetValue: number;
}

export interface Badge {
  id: string;
  name: string;
  description: string;
  icon?: string | null;
  rarity: string;
  restaurantId: string | null;
  unlockedAt: string;
}

export interface PassportData {
  customer: { name: string | null; totalOrders: number; totalSpent: number; loyaltyPoints: number };
  achievements: Achievement[];
  badges: Badge[];
  upcoming: UpcomingAchievement[];
  restaurants: RestaurantProgress[];
}

export interface FriendCard {
  id: string;
  displayName: string;
  photoURL: string | null;
  totalOrders: number;
  totalPoints: number;
  level: string;
  referralCode: string;
  stamps: { id: string; name: string }[];
  memberSince: string;
}

export interface FriendsData {
  referralCode: string;
  friends: (FriendCard & { friendshipId: string })[];
  incoming: { friendshipId: string; friend: FriendCard }[];
  outgoing: { friendshipId: string; friend: FriendCard }[];
}

export type ChallengeType = 'RESTAURANTS' | 'ORDERS' | 'CATEGORIES';
export type ChallengeStatus = 'PENDING' | 'ACTIVE' | 'COMPLETED' | 'DECLINED' | 'CANCELLED';

export interface ChallengeStake {
  type: 'points' | 'badge';
  amount?: number;
  badgeId?: string;
  badgeName?: string;
}

export interface ChallengeUser {
  id: string;
  displayName: string;
  totalPoints: number;
}

export interface Challenge {
  id: string;
  createdAt: string;
  deadline: string;
  type: ChallengeType;
  status: ChallengeStatus;
  initiatorId: string;
  opponentId: string;
  initiator: ChallengeUser;
  opponent: ChallengeUser;
  initiatorStake: ChallengeStake;
  opponentStake?: ChallengeStake;
  initiatorScore: number;
  opponentScore: number;
  winnerId?: string | null;
  winner?: ChallengeUser | null;
  resolvedAt?: string | null;
}

export interface HistoryOrder {
  id: string;
  createdAt: string;
  status: string;
  totalAmount: number;
  restaurant: { id: string; name: string };
  items: { name: string; quantity: number }[];
}

export type PassportTab = 'passport' | 'friends' | 'challenges' | 'history';

export interface FoodPassportPanelProps {
  onBackToOrder: () => void;
  onShowAuth?: () => void;
  justLoggedIn?: { customer: Customer; token: string } | null;
  standalone?: boolean;
}

// Deterministic emoji per restaurant — stable regardless of list order
export const RESTAURANT_EMOJIS = ['🍔', '🍕', '🍜', '🍣', '🌮', '🥘', '🍝', '🥗', '🍱', '🍛', '🥪', '☕'];

export const stableEmoji = (restaurantId: string): string => {
  let hash = 0;
  for (let i = 0; i < restaurantId.length; i++) {
    hash = (hash * 31 + restaurantId.charCodeAt(i)) & 0xFFFFFF;
  }
  return RESTAURANT_EMOJIS[Math.abs(hash) % RESTAURANT_EMOJIS.length];
};
